/**
 * Fase 28 · Bug C · Gestión de Equipo scoped a la empresa activa (Admin+).
 *
 * Admin puede:
 *  - Ver miembros de su empresa activa (con módulos por membresía).
 *  - Activar/desactivar miembros (excepto super master y a sí mismo).
 *  - Editar módulos de la membresía (dentro de lo que la empresa habilita).
 *
 * NO puede: crear/eliminar cuentas globales (eso queda para el Master en
 * /equipo). Master también puede usar esta vista para acciones rápidas
 * scoped a la empresa activa.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import { MODULES, ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Users, RefreshCw, Power, PowerOff, Settings } from "lucide-react";
import { toast } from "sonner";

export default function TeamCompany() {
  const { active } = useCompany();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogModules, setDialogModules] = useState({});

  const load = async () => {
    if (!active?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get("/team/company-members");
      setMembers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar miembros");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active?.id]);

  const toggleActive = async (m) => {
    if (m.is_super_master) {
      toast.error("No se puede modificar al Super Master");
      return;
    }
    if (m.user_id === user.id) {
      toast.error("No puede modificarse a sí mismo");
      return;
    }
    try {
      await api.patch(`/team/company-members/${m.user_id}/active`, { active: !m.active });
      toast.success(m.active ? "Miembro desactivado" : "Miembro reactivado");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    }
  };

  const openEditModules = (m) => {
    setEditing(m);
    setDialogModules({ ...(m.modules || {}) });
  };

  const saveModules = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/team/company-members/${editing.user_id}/modules`, {
        modules: dialogModules,
      });
      toast.success("Módulos actualizados");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setSaving(false); }
  };

  const companyModules = active?.modules || {};

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="team-company-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="divider-gold mb-3" />
          <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
            <Users className="h-8 w-8 text-brand-emerald" /> Gestión de Equipo
          </h1>
          <p className="text-sm text-stone-500 mt-2 max-w-2xl">
            Miembros con acceso a <strong>{active?.name || "…"}</strong>. Puedes activar,
            desactivar y ajustar módulos por miembro dentro de esta empresa.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-team-company">
          <RefreshCw className="h-4 w-4 mr-2" /> Recargar
        </Button>
      </div>

      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50/60">
              <TableHead>Miembro</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Módulos activos</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-16">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
              </TableCell></TableRow>
            ) : members.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-16 text-stone-500">
                Sin miembros en esta empresa.
              </TableCell></TableRow>
            ) : (
              members.map((m) => {
                const activeMods = MODULES.filter((mo) => m.modules?.[mo.key]);
                return (
                  <TableRow key={m.user_id} data-testid={`team-company-row-${m.user_id}`}>
                    <TableCell>
                      <div className="text-sm font-semibold text-brand-ink">{m.name}</div>
                      <div className="text-xs text-stone-500">{m.email}</div>
                      {m.is_super_master && (
                        <Badge className="mt-1 bg-brand-gold-soft text-brand-gold-dark border border-brand-gold/40 text-[9px]">
                          Super Master
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-stone-100 text-stone-700 border border-stone-200">
                        {ROLE_LABELS[m.role] || m.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {activeMods.length === 0 ? (
                          <span className="text-xs text-stone-400">—</span>
                        ) : (
                          activeMods.slice(0, 5).map((mo) => (
                            <Badge key={mo.key} className="bg-brand-emerald-soft text-brand-emerald-dark border border-brand-emerald/30 text-[9px]">
                              {mo.label}
                            </Badge>
                          ))
                        )}
                        {activeMods.length > 5 && (
                          <span className="text-[10px] text-stone-400">+{activeMods.length - 5}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {m.active ? (
                        <Badge className="bg-brand-emerald-soft text-brand-emerald-dark border border-brand-emerald/20">Activo</Badge>
                      ) : (
                        <Badge className="bg-stone-100 text-stone-500 border border-stone-200">Inactivo</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="icon" variant="ghost"
                          onClick={() => openEditModules(m)}
                          disabled={m.is_super_master || m.role === "master"}
                          data-testid={`edit-modules-${m.user_id}`}
                          title="Editar módulos"
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon" variant="ghost"
                          onClick={() => toggleActive(m)}
                          disabled={m.is_super_master || m.user_id === user?.id}
                          data-testid={`toggle-active-${m.user_id}`}
                          className={m.active ? "text-destructive hover:bg-destructive/10" : "text-brand-emerald-dark hover:bg-brand-emerald-soft"}
                          title={m.active ? "Desactivar" : "Activar"}
                        >
                          {m.active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-modules-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              Editar módulos · {editing?.name}
            </DialogTitle>
            <DialogDescription>
              Solo puedes habilitar módulos que la empresa <strong>{active?.name}</strong> tenga
              activos. Los módulos deshabilitados a nivel empresa no aparecen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {MODULES.filter((mo) => companyModules?.[mo.key]).map((mo) => (
              <label key={mo.key} className="flex items-center justify-between border border-border rounded px-3 py-2 bg-white">
                <span className="text-sm text-brand-ink">{mo.label}</span>
                <Switch
                  checked={!!dialogModules[mo.key]}
                  onCheckedChange={(v) => setDialogModules((prev) => ({ ...prev, [mo.key]: v }))}
                  data-testid={`edit-mod-toggle-${mo.key}`}
                />
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button
              onClick={saveModules} disabled={saving}
              className="bg-brand-emerald text-white hover:bg-brand-emerald-dark"
              data-testid="edit-modules-save"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
